import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArchiveX, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Button } from 'src/components/ui/button';
import {
  ConfirmDialog,
  ConfirmDialogAction,
  ConfirmDialogCancel,
  ConfirmDialogContent,
  ConfirmDialogDescription,
  ConfirmDialogFooter,
  ConfirmDialogHeader,
  ConfirmDialogTitle,
  ConfirmDialogTrigger,
} from 'src/components/ui/confirm-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { If } from 'src/components/If';
import { useToast } from 'src/components/ui/use-toast';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useDecoratedRequestOutcomeContext } from 'src/features/requests/request-outcome/use-decorated-request-outcome-context';
import { useRequestQueryMutationDecorator } from 'src/features/requests/use-request-query-mutation-decorator';
import { useRequestOutcomesQueryMutationDecorator } from 'src/features/requests/request-outcome/use-request-outcomes-query-mutation-decorator';
import {
  archivedOutcomesFilters,
  deliverableOutcomesFilters,
  pendingOutcomesFilters,
} from 'src/features/requests/request-outcome/filter-queries';
import { useRequestOutcomesQueryData } from 'src/features/requests/request-outcome/use-request-outcomes-query-context';
import { requestClient, requestOutcomeStatus } from 'src/lib/services/api/request-api';
import { RequestStatusBadge } from 'src/features/requests/request-status-badge';
import { resolveDisplayedRequestStatus } from 'src/features/requests/request.helper';
import { useCompanyId } from 'src/features/companies/use-company-id';
import { useOpenRequestStatusChangeModal } from 'src/features/requests/request-status-change/use-open-request-status-change-modal';
import {
  RequestDeliverableOutcomeCard,
  RequestDeliverableOutcomeCardBackgroundContent,
  RequestDeliverableOutcomeCardBackgroundContentFallback,
  RequestDeliverableOutcomeCardContent,
  RequestDeliverableOutcomeCardFooter,
  RequestDeliverableOutcomeCardForegroundContent,
  RequestDeliverableOutcomeCardHeader,
  RequestDeliverableOutcomeCardTitle,
} from 'src/features/requests/request-outcome/request-deliverable-outcome-card';
import { RequestOutcomeCreatedByIcon } from 'src/features/requests/request-outcome/pending-outcomes';
import { RequestDeliverableCardBadge } from 'src/features/requests/request-outcome/request-deliverable-outcome-card';
import { RequestOutcomeAttentionButton } from 'src/features/requests/request-outcome/request-outcome-attention-button';

const RequestDeliverableQueuedOutcomeCard: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const companyId = useCompanyId();
  const { openRequestStatusChangeModal } = useOpenRequestStatusChangeModal();

  const request = useDecoratedRequestContext();
  const outcome = useDecoratedRequestOutcomeContext();
  // Single source of truth for the pill: Needs Attention / Needs Re-edit
  // override the raw status (see resolveDisplayedRequestStatus). Previously the
  // raw status rendered a stale "Queued" pill beside a separate flag badge,
  // surfacing two conflicting status tags at once (#2510).
  const displayedStatus = resolveDisplayedRequestStatus(outcome.raw);
  const requestActions = useRequestQueryMutationDecorator(request.id);

  const pendingOutcomesActions = useRequestOutcomesQueryMutationDecorator(
    request.id,
    pendingOutcomesFilters,
  );
  const deliverablesOutcomesActions = useRequestOutcomesQueryMutationDecorator(
    request.id,
    deliverableOutcomesFilters,
  );
  const archivedOutcomesActions = useRequestOutcomesQueryMutationDecorator(
    request.id,
    archivedOutcomesFilters,
  );
  const deliverables = useRequestOutcomesQueryData(request.id, deliverableOutcomesFilters);

  const archiveOutcomeMutation = useMutation((id: string) => requestClient.archive(id));

  const handleArchive = () => {
    archiveOutcomeMutation.mutate(outcome.id, {
      onSuccess: () => {
        requestActions.invalidate();
        deliverablesOutcomesActions.remove(outcome.id);
        pendingOutcomesActions.remove(outcome.id);
        archivedOutcomesActions.push({
          ...outcome.raw,
          status: requestOutcomeStatus.archived,
        });
        deliverablesOutcomesActions.invalidate();
        pendingOutcomesActions.invalidate();
        archivedOutcomesActions.invalidate();
      },
      onError: () => {
        toast({
          title: t('Warning')!,
          description: t('There was an error processing your request'),
          variant: 'destructive',
        });
      },
    });
  };

  const items = deliverables?.items ?? [];
  const areAnyCurrentlyEditingOutcomes = items.some(
    (o) => o.status === requestOutcomeStatus.currentlyEditing,
  );

  // Open the SHARED status-change modal — the same one the outcome detail page
  // and the main request side panel use — passing the outcome id as requestId.
  // This gives the Deliverables tab the standard Editor note + Suite folder URL
  // fields and validation, replacing the old folder-URL-only dialog that never
  // captured an editor note (#2539). The parent's outcome lists refresh via
  // useChangeRequestStatusMutation's onSuccess (it refetches
  // [requestOutcomes, parent_id] on every transition), so the card moves
  // pending→deliverable without the manual optimistic updates we did before.
  const handleAction = () => {
    if (areAnyCurrentlyEditingOutcomes) {
      toast({
        title: t('Cannot Proceed')!,
        description: t('There is already an outcome in editing status')!,
        variant: 'destructive',
      });
      return;
    }

    openRequestStatusChangeModal({ requestId: outcome.id, companyId });
  };

  // Single, consistent CTA across all queued outcomes (#2524). The button's
  // job is to advance the outcome's workflow state, so it always reads "Change
  // status" — matching the button on the outcome detail page.
  const actionLabel = t('change_status', { ns: 'actions', defaultValue: 'Change status' });

  return (
    <RequestDeliverableOutcomeCard className={'tw-cursor-default'}>
      <RequestDeliverableOutcomeCardContent>
        <RequestDeliverableOutcomeCardBackgroundContent>
          <RequestDeliverableOutcomeCardBackgroundContentFallback />
        </RequestDeliverableOutcomeCardBackgroundContent>

        <RequestDeliverableOutcomeCardForegroundContent>
          <If when={archiveOutcomeMutation.isLoading}>
            <div
              className={
                'tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center tw-justify-center tw-rounded-md tw-bg-background/70'
              }
            >
              <Loader2 size={30} className={'tw-animate-spin tw-text-primary'} />
            </div>
          </If>

          <div
            className={
              'tw-flex tw-w-full tw-flex-wrap tw-items-start tw-justify-end tw-gap-2'
            }
          >
            <div className={'tw-flex tw-items-center tw-gap-2'}>
              <RequestOutcomeAttentionButton />
              <RequestStatusBadge variant={displayedStatus} />
            </div>
          </div>

          <Button
            variant={'accent1'}
            className={'tw-mt-auto tw-w-full'}
            disabled={areAnyCurrentlyEditingOutcomes}
            onClick={handleAction}
          >
            {actionLabel}
          </Button>
        </RequestDeliverableOutcomeCardForegroundContent>
      </RequestDeliverableOutcomeCardContent>

      <RequestDeliverableOutcomeCardHeader>
        <RequestDeliverableOutcomeCardTitle>{outcome.raw.title}</RequestDeliverableOutcomeCardTitle>
      </RequestDeliverableOutcomeCardHeader>

      <RequestDeliverableOutcomeCardFooter>
        <RequestDeliverableCardBadge>
          <RequestOutcomeCreatedByIcon size={16} createdBy={outcome.raw.created_by} />
        </RequestDeliverableCardBadge>

        <If when={outcome.canArchive}>
          <ConfirmDialog>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <ConfirmDialogTrigger asChild>
                    <Button
                      variant={'ghost'}
                      size={'icon'}
                      className={'tw-ms-auto tw-text-text-strong'}
                      disabled={archiveOutcomeMutation.isLoading}
                    >
                      <ArchiveX size={20} />
                    </Button>
                  </ConfirmDialogTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  {t('actions:archive', { defaultValue: 'Archive' })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <ConfirmDialogContent>
              <ConfirmDialogHeader>
                <ConfirmDialogTitle>
                  {t('actions:archive_outcome', { defaultValue: 'Archive outcome' })}
                </ConfirmDialogTitle>
              </ConfirmDialogHeader>
              <ConfirmDialogDescription
                dangerouslySetInnerHTML={{
                  __html: t('alerts:confirm.archive_outcome', {
                    replace: { title: outcome.raw.title },
                    defaultValue:
                      'Are you sure you want to archive <b class="tw-font-bold">:title</b>?',
                  })!,
                }}
              />
              <ConfirmDialogFooter>
                <ConfirmDialogCancel>
                  {t('actions:cancel', { defaultValue: 'Cancel' })}
                </ConfirmDialogCancel>
                <ConfirmDialogAction onClick={handleArchive}>
                  {t('actions:confirm', { defaultValue: 'Confirm' })}
                </ConfirmDialogAction>
              </ConfirmDialogFooter>
            </ConfirmDialogContent>
          </ConfirmDialog>
        </If>
      </RequestDeliverableOutcomeCardFooter>
    </RequestDeliverableOutcomeCard>
  );
};

export { RequestDeliverableQueuedOutcomeCard };
